import { BaseAction, selectCampaign } from './index'
import {
    CityType,
    ConstantsResponse,
    COUNTRY_ENUM,
    FollowerTierOption,
    FollowerTierFilterOption,
    LocationOption,
    LocationOptionsRequest,
    OccupationOption,
    PLATFORM_ENUM,
    ProfileOptionsRequest,
    ProfileOptionsResponse,
    Tag,
    TagCategories,
    TASK_TYPE_ENUM,
    TaskTypes,
    PlatformContentType,
    PLATFORM_CONTENT_TYPE_ENUM,
    STEP_TYPE_ENUM,
    ProfileTypeOption,
    AddTagPayload,
    GetTagsPayload,
    BankDetail,
} from '../services/api/constants'
import { State } from './'
import i18n from 'config/i18n'
import { createSelector } from 'reselect'
import { selectEditingCampaignCountry, CampaignState } from './campaigns'
import { selectReviewingCampaignCountry, selectSelectedCampaignUuid } from './review'
import { RegistrationResultState } from 'state/registrations'
import { PartiposterProfile } from 'state/submissions'
import { RegistrationBlock } from 'services/api/registrations'
import { parseFollowerOption } from 'services/numbers'
import _ from 'lodash'
import { StepState, TaskState, selectAllSteps } from './tasks'
import { BUSINESS_TYPE } from './teams'
import {
    bannerOption,
    getSubmissionRejectionsReviewTranslation,
    getSubmissionRejectionsTranslation,
    getSubmissionTagReviewTranslation,
    getSubmissionTagTranslation,
    getTagCategoryRejectionsTranslation,
    getTagCategoryTranslation,
} from 'components/utils/translationsMap'
import { getStepTypeAvailabiltyStatus } from 'components/utils'
import { TAG_LABELS } from 'components/Tag'

export enum TagCategoryEnum {
    INFLUENCER_TYPE = 'profile_influencer_type',
    PHOTO_QUALITY = 'profile_photo_quality',
    CAPTION_QUALITY = 'profile_caption_quality',
    SUBMISSION_TAG = 'submission_tag',
    PROFILE_TYPE = 'profile_type',
}

export enum TagCategoryTargetEnum {
    PROFILE = 'profile',
    SUBMISSION = 'submission',
    SUBMISSION_EDITS = 'submission_edits',
    SUBMISSION_REJECTIONS = 'submission_rejections',
    PROFILE_TYPE = 'profile_type',
}

export interface TagCategoryState extends TagCategories {
    tags: Tag[]
}

export type ProfileTagEntitiesState = {
    [category: string]: TagCategoryState
}

export type TagByIdEntitiesState = {
    [category: string]: Tag
}

export type TagCategoryByIdEntitiesState = {
    [uuid: string]: TagCategories
}

export interface GroupedOption {
    label: string
    options: TagOption[]
}

export interface DisplayGroupedOption extends GroupedOption {
    isDisabled?: boolean
}

export type TagOption = {
    uuid: string
    enum: string
    label: string
    value: string
    category: string
}

interface LocationOptionData {
    isLoading: boolean
    data?: LocationOption[]
}

export enum GLOBAL_MODAL_TYPE {
    REQUEST_EXPORT = 'REQUEST_EXPORT',
    MASS_APPROVAL = 'MASS_APPROVAL',
    INTEREST_REGISTRATION_WAITLIST_MASS_APPROVAL = 'INTEREST_REGISTRATION_WAITLIST_MASS_APPROVAL',
    INTEREST_REGISTRATION_MASS_APPROVAL_EXCEED = 'INTEREST_REGISTRATION_MASS_APPROVAL_EXCEED',
    INTEREST_REGISTRATION_ACTION_DETAIL = 'INTEREST_REGISTRATION_ACTION_DETAIL',
    INTEREST_REGISTRATION_BACK_TO_WAITLIST = 'INTEREST_REGISTRATION_BACK_TO_WAITLIST',
    BANNER_DISABLE = 'BANNER_DISABLE',
    BANNER_CREATE = 'BANNER_CREATE',
    BRIEF_REQUIREMENT = 'BRIEF_REQUIREMENT',
}

export interface ActionModalProps {
    registration: RegistrationResultState
    registrationFormBlocksById: Record<string, RegistrationBlock>
    campaignUuid: string
    profile: PartiposterProfile
    igUserName?: string
    hasFbTask?: boolean
    fbUserName?: string
    onOpenActionDetailModal?: any
}
interface OpenModalState {
    type?: GLOBAL_MODAL_TYPE
    count?: number
    task?: TaskState
    stepId?: string
    actionProps?: ActionModalProps
}

type BlockTypeConfigState = {
    config: {
        data: {
            question_number_required?: boolean
            number_of_photos_required?: boolean
            is_long_answer_required?: boolean
        }
        description_required: boolean
        images_required: boolean
        is_required_required: boolean
        title_required: boolean
    }
    id: string
    label: string
}

type SubmissionStatusState = {
    id: string
    label: string
}

export type LengthLimitState = {
    blocks: {
        description: number
        option: number
        title: number
    }
    registration_form: {
        title: number
        description: number
    }
}

export type RegistrationConfigState = {
    block_types: BlockTypeConfigState[]
    length_limits: LengthLimitState
    submission_status: SubmissionStatusState[]
}

type BannerOption = typeof bannerOption

export type BANNER_OPTION_TYPE = BannerOption[keyof typeof bannerOption]

export type BannerOptionState = {
    id: number
    label: BANNER_OPTION_TYPE
}

export type BannerConfigState = {
    banner_status: BannerOptionState[]
    deeplink_types: BannerOptionState[]
}

export type OptionTypes = {
    id: string
    label: string
}

export interface ConstantsState extends ConstantsResponse {
    platforms: { id: PLATFORM_ENUM; name: string }[]
    profileTags: string[]
    submissionTags: string[]
    tagCategoryByCategoryEnum: ProfileTagEntitiesState
    tagById: TagByIdEntitiesState
    tagCategoryById: TagCategoryByIdEntitiesState
    editTagGroupOptions: GroupedOption[]
    rejectionTagGroupOptions: GroupedOption[]
    locationOptions: {
        [key: string]: Record<string, LocationOptionData | Record<string, LocationOptionData>>
    }
    profileOptions: ProfileOptionsResponse
    openModal: OpenModalState
    registration: RegistrationConfigState
    banner: BannerConfigState
    profiles: {
        business_types: OptionTypes[]
    }
    brands: {
        industry: OptionTypes[]
        member_range: OptionTypes[]
    }
    campaign_reports: {
        social_media_follower_number_tiers: FollowerTierOption[]
    }
}

const initialState: ConstantsState = {
    platforms: [],
    country_settings: {} as any,
    poll_categories: undefined,
    poll_section_types: undefined,
    rejection_reason_types: undefined,
    campaign: undefined,
    poll_status: undefined,
    profileTags: [], // enum list
    submissionTags: [], // enum list
    tagCategoryByCategoryEnum: {},
    tagById: {},
    tagCategoryById: {},
    editTagGroupOptions: [], //enum list
    rejectionTagGroupOptions: [], //enum list
    locationOptions: {},
    profileOptions: {
        occupations: [],
        social_media_follower_number_tiers: [],
        profile_types: [],
    },
    openModal: {},
    registration: {
        block_types: [],
        length_limits: {
            blocks: {
                description: 500,
                option: 100,
                title: 100,
            },
            registration_form: {
                title: 200,
                description: 1000,
            },
        },
        submission_status: [],
    },
    banner: {
        banner_status: [],
        deeplink_types: [],
    },
    profiles: {
        business_types: [],
    },
    brands: {
        industry: [],
        member_range: [],
    },
    campaign_reports: {
        social_media_follower_number_tiers: [],
    },
}

export enum ConstantsActions {
    CONSTANTS_REQUEST = 'constants/CONSTANTS_REQUEST',
    SAVE_DETAILS = 'constants/SAVE_DETAILS',
    GET_LOCATION_OPTIONS_REQUEST = 'constants/GET_LOCATION_OPTIONS_REQUEST',
    GET_LOCATION_OPTIONS_REQUEST_SUCCESS = 'constants/GET_LOCATION_OPTIONS_REQUEST_SUCCESS',
    GET_PROFILE_OPTIONS_REQUEST = 'constants/GET_PROFILE_OPTIONS_REQUEST',
    GET_PROFILE_OPTIONS_REQUEST_SUCCESS = 'constants/GET_PROFILE_OPTIONS_REQUEST_SUCCESS',
    OPEN_MODAL = 'constants/OPEN_MODAL',
    OPEN_MODAL_WITH_PROPS = 'constants/OPEN_MODAL_WITH_PROPS',
    CLOSE_MODAL = 'constants/CLOSE_MODAL',
    ADD_CUSTOM_TAG = 'constants/ADD_CUSTOM_TAG',
    GET_TAGS = 'constants/GET_TAGS',
    UPDATE_CATEGORY_TAGS = 'constants/UPDATE_CATEGORY_TAGS',
}

interface SaveConstantsPayload {
    data: ConstantsResponse | undefined
}

export const saveConstants = (payload: SaveConstantsPayload) => ({
    payload,
    type: ConstantsActions.SAVE_DETAILS,
})

export const requestLocationOptions = (payload: LocationOptionsRequest) => ({
    payload,
    type: ConstantsActions.GET_LOCATION_OPTIONS_REQUEST,
})

export const addCustomTagRequest = (payload: AddTagPayload) => ({
    payload,
    type: ConstantsActions.ADD_CUSTOM_TAG,
})

interface SetLocationOptionsPayload {
    country: COUNTRY_ENUM
    idName: string
    parent: number | undefined
    options: LocationOption[] | undefined
}

export const setLocationOptions = (payload: SetLocationOptionsPayload) => ({
    payload,
    type: ConstantsActions.GET_LOCATION_OPTIONS_REQUEST_SUCCESS,
})

export const saveProfileOptions = (payload: SaveProfileOptionsPayload) => ({
    payload,
    type: ConstantsActions.GET_PROFILE_OPTIONS_REQUEST_SUCCESS,
})

interface SaveProfileOptionsPayload {
    data: ProfileOptionsResponse | undefined
}

export const requestProfileOptions = (payload: ProfileOptionsRequest) => ({
    payload,
    type: ConstantsActions.GET_PROFILE_OPTIONS_REQUEST,
})

export const openModal = (payload: GLOBAL_MODAL_TYPE) => ({
    payload,
    type: ConstantsActions.OPEN_MODAL,
})

export const openModalWithProps = (payload: OpenModalState) => ({
    payload,
    type: ConstantsActions.OPEN_MODAL_WITH_PROPS,
})

export const closeModal = () => ({
    type: ConstantsActions.CLOSE_MODAL,
})

export const getConstantTags = (payload: GetTagsPayload) => ({
    payload,
    type: ConstantsActions.GET_TAGS,
})

interface UpdateCategoryTagsPayload {
    categoryEnum: string
    tags: Tag[]
}

export const updateCategoryTags = (payload: UpdateCategoryTagsPayload) => ({
    payload,
    type: ConstantsActions.UPDATE_CATEGORY_TAGS,
})

export const constantsReducer = (state: ConstantsState = initialState, action: BaseAction) => {
    switch (action.type) {
        case ConstantsActions.SAVE_DETAILS:
            const { data }: { data: ConstantsResponse } = action.payload
            const profileTags: string[] = []
            const submissionTags: string[] = []
            const tagCategoryByCategoryEnum: ProfileTagEntitiesState = {}
            const tagCategoryById: TagCategoryByIdEntitiesState = {}
            const editTagGroupOptions: GroupedOption[] = []
            const rejectionTagGroupOptions: GroupedOption[] = []
            data?.tag_categories?.forEach((tagCategory: TagCategories) => {
                if (tagCategory.target === TagCategoryTargetEnum.PROFILE) {
                    profileTags.push(tagCategory.enum)
                } else if (tagCategory.target === TagCategoryTargetEnum.SUBMISSION) {
                    submissionTags.push(tagCategory.enum)
                } else if (tagCategory.target === TagCategoryTargetEnum.SUBMISSION_EDITS) {
                    editTagGroupOptions.push({
                        label: getTagCategoryTranslation[tagCategory.enum](i18n.t),
                        options:
                            data && data.tags
                                ? data.tags
                                      .filter((tag: Tag) => tag.category?.uuid === tagCategory.uuid)
                                      .map(tag => {
                                          return {
                                              uuid: tag.uuid,
                                              enum: tag.name,
                                              label: getSubmissionTagTranslation[tag.name](i18n.t),
                                              value: getSubmissionTagReviewTranslation[tag.name](i18n.t),
                                              category: tagCategory.uuid,
                                          }
                                      })
                                : [],
                    })
                } else if (tagCategory.target === TagCategoryTargetEnum.SUBMISSION_REJECTIONS) {
                    rejectionTagGroupOptions.push({
                        label: getTagCategoryRejectionsTranslation[tagCategory.enum](i18n.t),
                        options:
                            data && data.tags
                                ? data.tags
                                      .filter((tag: Tag) => tag.category?.uuid === tagCategory.uuid)
                                      .map(tag => {
                                          return {
                                              uuid: tag.uuid,
                                              enum: tag.name,
                                              label: getSubmissionRejectionsTranslation[tag.name](i18n.t),
                                              value: getSubmissionRejectionsReviewTranslation[tag.name](i18n.t),
                                              category: tagCategory.uuid,
                                          }
                                      })
                                : [],
                    })
                }

                tagCategoryByCategoryEnum[tagCategory.enum] = {
                    ...tagCategory,
                    tags:
                        data && data.tags
                            ? data.tags.filter((tag: Tag) => tag.category?.uuid === tagCategory.uuid)
                            : [],
                }
                tagCategoryById[tagCategory.uuid] = tagCategory
            })
            return Object.assign({}, state, {
                ...data,
                profileTags,
                submissionTags,
                tagCategoryByCategoryEnum,
                tagById:
                    data && data.tags
                        ? data.tags.reduce(
                              (memo: TagByIdEntitiesState, tag: Tag) => Object.assign({}, memo, { [tag.uuid]: tag }),
                              {},
                          )
                        : {},
                tagCategoryById,
                editTagGroupOptions,
                rejectionTagGroupOptions,
            })
        case ConstantsActions.UPDATE_CATEGORY_TAGS:
            const { categoryEnum, tags } = action.payload
            const newTagCategoryByCategoryEnum = { ...state.tagCategoryByCategoryEnum }
            const newTagById = { ...state.tagById }
            tags.forEach((tag: Tag) => {
                if (!newTagById[tag.uuid]) {
                    newTagById[tag.uuid] = tag
                }
            })
            newTagCategoryByCategoryEnum[categoryEnum] = {
                ...newTagCategoryByCategoryEnum[categoryEnum],
                tags: tags,
            }
            return Object.assign({}, state, {
                tagCategoryByCategoryEnum: newTagCategoryByCategoryEnum,
                tagById: newTagById,
            })
        case ConstantsActions.GET_LOCATION_OPTIONS_REQUEST_SUCCESS:
            const { options, country, idName, parent }: SetLocationOptionsPayload = action.payload
            if (parent) {
                return {
                    ...state,
                    locationOptions: {
                        [country]: {
                            ...state.locationOptions[country],
                            [idName]: {
                                ...(state.locationOptions[country]
                                    ? state.locationOptions[country][idName]
                                    : undefined),
                                [parent]: {
                                    isLoading: false,
                                    data: options,
                                },
                            },
                        },
                    },
                }
            }
            return {
                ...state,
                locationOptions: {
                    [country]: {
                        ...state.locationOptions[country],
                        [idName]: {
                            isLoading: false,
                            data: options,
                        },
                    },
                },
            }
        case ConstantsActions.GET_LOCATION_OPTIONS_REQUEST:
            const {
                country: request_country,
                parent: request_parent,
                type_id_name: request_idName,
            }: LocationOptionsRequest = action.payload
            if (request_parent) {
                return {
                    ...state,
                    locationOptions: {
                        [request_country]: {
                            ...state.locationOptions[request_country],
                            [request_idName]: {
                                ...(state.locationOptions[request_country]
                                    ? state.locationOptions[request_country][request_idName]
                                    : undefined),
                                [request_parent]: {
                                    isLoading: true,
                                },
                            },
                        },
                    },
                }
            }
            return {
                ...state,
                locationOptions: {
                    [request_country]: {
                        ...state.locationOptions[request_country],
                        [request_idName]: {
                            isLoading: true,
                        },
                    },
                },
            }
        case ConstantsActions.GET_PROFILE_OPTIONS_REQUEST_SUCCESS:
            return Object.assign({}, state, { profileOptions: action.payload.data })
        case ConstantsActions.OPEN_MODAL:
            return Object.assign({}, state, { openModal: { type: action.payload } })
        case ConstantsActions.OPEN_MODAL_WITH_PROPS:
            return Object.assign({}, state, { openModal: { ...action.payload } })
        case ConstantsActions.CLOSE_MODAL:
            return Object.assign({}, state, { openModal: undefined })
        default:
            return state
    }
}

export const selectConstants = (state: State) => state.constants
export const selectTeamConstants = (state: State) => selectConstants(state).teams
export const selectCampaignConstants = (state: State) => selectConstants(state).campaign
export const selectCampaignTaskTypes = (state: State) => {
    const campaignConstants = selectCampaignConstants(state)
    return campaignConstants ? campaignConstants.task_types : []
}
export const selectTaskPlatformContentTypes = (state: State): PlatformContentType[] => {
    const campaignConstants = selectCampaignConstants(state)
    return campaignConstants ? campaignConstants.platform_content_types : []
}
export const selectTaskPlatformContentTypeOptions = createSelector(
    selectTaskPlatformContentTypes,
    platformContentTypes =>
        platformContentTypes.map(item => {
            let label = item.name
            switch (item.name) {
                case PLATFORM_CONTENT_TYPE_ENUM.INSTAGRAM_STORY:
                    label = i18n.t('taskType.instagramStory')
                    break
                case PLATFORM_CONTENT_TYPE_ENUM.INSTAGRAM_POST:
                    label = i18n.t('taskType.instagramPost')
                    break
                case PLATFORM_CONTENT_TYPE_ENUM.INSTAGRAM_REEL:
                    label = i18n.t('taskType.instagramReel')
                    break
                case PLATFORM_CONTENT_TYPE_ENUM.TIKTOK_POST:
                    label = i18n.t('taskType.tikTok')
                    break
                case PLATFORM_CONTENT_TYPE_ENUM.TWITTER_POST:
                    label = i18n.t('taskType.twitter')
                    break
                case PLATFORM_CONTENT_TYPE_ENUM.FACEBOOK_POST:
                    label = i18n.t('taskType.facebook')
                    break
                case PLATFORM_CONTENT_TYPE_ENUM.YOUTUBE_VIDEO:
                    label = i18n.t('taskType.youtube')
                    break
                case PLATFORM_CONTENT_TYPE_ENUM.DOWNLOAD_APP:
                    label = i18n.t('taskType.downloadApp')
                    break
                case PLATFORM_CONTENT_TYPE_ENUM.WRITE_REVIEW:
                    label = i18n.t('taskType.review')
                    break
                case PLATFORM_CONTENT_TYPE_ENUM.FOLLOW_ACCOUNT:
                    label = i18n.t('taskType.followSocialMediaAccount')
                    break
                case PLATFORM_CONTENT_TYPE_ENUM.PURCHASE_PRODUCT:
                    label = i18n.t('taskType.purchaseProduct')
                    break
                case PLATFORM_CONTENT_TYPE_ENUM.ATTEND_EVENT:
                    label = i18n.t('taskType.attendEvent')
                    break
                case PLATFORM_CONTENT_TYPE_ENUM.REFERRAL:
                    label = i18n.t('taskType.referral')
                    break
                case PLATFORM_CONTENT_TYPE_ENUM.USER_TUTORIAL:
                    label = i18n.t('taskType.userTutorial')
                    break
                case PLATFORM_CONTENT_TYPE_ENUM.PAYOUTS:
                    label = i18n.t('taskType.payout')
                    break
                case PLATFORM_CONTENT_TYPE_ENUM.VIDEO_CREATION:
                    label = i18n.t('taskType.videoCreation')
                    break
                case PLATFORM_CONTENT_TYPE_ENUM.PHOTO_CREATION:
                    label = i18n.t('taskType.photoCreation')
                    break
                case PLATFORM_CONTENT_TYPE_ENUM.OTHERS:
                    label = i18n.t('general.option.others')
                    break
                case PLATFORM_CONTENT_TYPE_ENUM.XIAOHONGSHU_POST:
                    label = i18n.t('taskType.xiaohongshuPost')
                    break
            }
            return { value: item.name, label }
        }),
)
export const selectTaskPlatformContentTypesByName = createSelector(
    selectTaskPlatformContentTypes,
    platformContentTypes => _.keyBy(platformContentTypes, item => item.name),
)

export const selectTaskSetting = createSelector(
    [selectCampaignTaskTypes, (_: State, props: { taskType?: TASK_TYPE_ENUM }) => props.taskType],
    (taskTypes, taskType) => {
        return taskTypes && taskType ? _.keyBy(taskTypes, 'name')[taskType] : ({} as TaskTypes)
    },
)
export const selectCampaignStepTypes = (state: State) => {
    const campaignConstants = selectCampaignConstants(state)
    return campaignConstants ? campaignConstants.step_types : []
}
export const selectTaskStepSettings = createSelector(
    [selectCampaignStepTypes, (_: State, props: { task?: TaskState }) => props.task],
    (stepTypes, task) => {
        const selectedStepTypes = task ? task.steps.map((step: StepState) => step.type) : []
        return stepTypes
            ? _.keyBy(
                  stepTypes.filter(stepSetting => {
                      return _.includes(selectedStepTypes, stepSetting.name)
                  }),
                  'name',
              )
            : {}
    },
)
export const selectIsEngagementStepTypeHelper = createSelector(selectCampaignStepTypes, stepTypes => {
    const engagementStepTypes = stepTypes
        .filter(stepType => stepType.config.is_engagement_submission_step)
        .map(stepType => stepType.name)

    return (stepType: string) => engagementStepTypes.indexOf(stepType as STEP_TYPE_ENUM) > -1
})
export const selectIsUrlOrScreenshotSubmissionStepTypeHelper = createSelector(selectCampaignStepTypes, stepTypes => {
    const urlOrScreenshotSubmissionStepTypes = stepTypes
        .filter(stepType => stepType.config.is_url_or_screenshot_submission_step)
        .map(stepType => stepType.name)

    return (stepType: string) => urlOrScreenshotSubmissionStepTypes.indexOf(stepType as STEP_TYPE_ENUM) > -1
})
export const selectAvailableStepStatus = createSelector(
    selectAllSteps,
    selectIsEngagementStepTypeHelper,
    selectIsUrlOrScreenshotSubmissionStepTypeHelper,
    (stepList, isEngagementStepType, isUrlOrScreenshotSubmissionStepType) =>
        getStepTypeAvailabiltyStatus({ stepList, isEngagementStepType, isUrlOrScreenshotSubmissionStepType }),
)

export const createGetKey = (constantsKey: string) => (state: ConstantsState) => (state as any)[constantsKey]

export const selectTagCategory = (enumCategory: TagCategoryEnum) => (state: State) =>
    selectConstants(state).tagCategoryByCategoryEnum[enumCategory]

export const selectMultipleTagCategories = (enumCategories: TagCategoryEnum[]) =>
    createSelector(
        state => selectConstants(state as State).tagCategoryByCategoryEnum,
        tagCategoryByCategoryEnum => {
            return enumCategories.reduce((acc: any, category) => {
                if (tagCategoryByCategoryEnum[category]) {
                    acc[category] = tagCategoryByCategoryEnum[category].tags
                }
                return acc
            }, {})
        },
    )

const sortTagsByLabel = <T>(data: T[], getLabel: (option: T) => string): T[] => {
    return data.sort((a, b) => {
        const aIsEnglish = /^[A-Za-z]/.test(getLabel(a))
        const bIsEnglish = /^[A-Za-z]/.test(getLabel(b))

        if (aIsEnglish && !bIsEnglish) {
            return -1
        } else if (!aIsEnglish && bIsEnglish) {
            return 1
        } else {
            return getLabel(a)?.localeCompare(getLabel(b))
        }
    })
}

export const selectSortedPartiposterTagsFilter = createSelector(
    [
        (state: State) => selectTagCategory(TagCategoryEnum.SUBMISSION_TAG)(state),
        (state: State) => selectTagCategory(TagCategoryEnum.INFLUENCER_TYPE)(state),
        (state: State) => selectTagCategory(TagCategoryEnum.PHOTO_QUALITY)(state),
        (state: State) => selectTagCategory(TagCategoryEnum.CAPTION_QUALITY)(state),
        (state: State) => selectTagCategory(TagCategoryEnum.PROFILE_TYPE)(state),
    ],
    (submissionTags, influencerTypeTags, photoQualityTags, captionQualityTags, profileTypeTags) => {
        return sortTagsByLabel(
            [
                ...submissionTags.tags,
                ...influencerTypeTags.tags,
                ...photoQualityTags.tags,
                ...captionQualityTags.tags,
                ...profileTypeTags.tags,
            ].map(tag => ({
                ...tag,
                name:
                    tag.category.target === TagCategoryTargetEnum.PROFILE_TYPE || tag.is_custom
                        ? tag.name
                        : TAG_LABELS[tag.name],
            })),
            option => option.name,
        )
    },
)

export const selectSortedSubmissionTags = createSelector(
    [
        (state: State) => selectTagCategory(TagCategoryEnum.SUBMISSION_TAG)(state),
        (state: State) => selectTagCategory(TagCategoryEnum.PROFILE_TYPE)(state),
    ],
    (submissionTags, profileTypeTags) => {
        const standard: TagOption[] = []
        const custom: TagOption[] = []

        submissionTags.tags.forEach(t => {
            if (t.is_custom) {
                custom.push({
                    uuid: t.uuid,
                    label: t.name,
                    value: t.uuid,
                    enum: t.name,
                    category: t.category.uuid,
                })
            } else {
                standard.push({
                    uuid: t.uuid,
                    label: TAG_LABELS[t.name] || t.name,
                    value: t.uuid,
                    enum: t.name,
                    category: t.category.uuid,
                })
            }
        })

        profileTypeTags?.tags?.forEach(t => {
            custom.push({
                uuid: t.uuid,
                label: t.name,
                value: t.uuid,
                enum: t.name,
                category: t.category.uuid,
            })
        })
        return {
            standard: standard,
            custom: sortTagsByLabel(custom, option => option.label),
        }
    },
)

export const selectTagCategoryById = (uuid: string) => (state: State) => selectConstants(state).tagCategoryById[uuid]

export const selectTagById = (state: State) => selectConstants(state).tagById

export const selectEditTagGroupOptions = () => (state: State) => state.constants.editTagGroupOptions

export const selectRejectionTagGroupOptions = () => (state: State) => state.constants.rejectionTagGroupOptions

export const selectCountryList = (state: State) => {
    const countrySettings = selectConstants(state).country_settings
    return Object.keys(countrySettings).map(countryCode => {
        let label = countryCode
        switch (countryCode) {
            case COUNTRY_ENUM.SG:
                label = i18n.t('country.singapore')
                break
            case COUNTRY_ENUM.ID:
                label = i18n.t('country.indonesia')
                break
            case COUNTRY_ENUM.TW:
                label = i18n.t('country.taiwan')
                break
            case COUNTRY_ENUM.PH:
                label = i18n.t('country.philippines')
                break
            case COUNTRY_ENUM.MY:
                label = i18n.t('country.malaysia')
                break
            case COUNTRY_ENUM.HK:
                label = i18n.t('country.hongKong')
                break
            case COUNTRY_ENUM.TH:
                label = i18n.t('country.thailand')
                break
            case COUNTRY_ENUM.VN:
                label = i18n.t('country.vietnam')
                break
        }

        return {
            id: countryCode,
            label,
        }
    })
}

export const selectCountrySetting = (countryCode?: COUNTRY_ENUM | null) => (state: State) => {
    if (!countryCode) {
        return null
    } else {
        return selectConstants(state).country_settings[countryCode]
    }
}

const selectLocationOptions = (state: State) => selectConstants(state).locationOptions

export const makeLocationOptionsByCountrySelector = (type: 'edit' | 'review', idName: string, parent?: number | null) =>
    createSelector(
        selectLocationOptions,
        type === 'review' ? selectReviewingCampaignCountry : selectEditingCampaignCountry,
        (locationOptions, country): LocationOptionData => {
            const noData = { isLoading: false }
            try {
                if (!country || !locationOptions[country] || parent === null) return noData

                let result
                if (parent) {
                    result = (locationOptions[country][idName] as Record<string, LocationOptionData>)[parent]
                } else {
                    result = (locationOptions[country] as Record<string, LocationOptionData>)[idName]
                }
                return result ? result : noData
            } catch (e) {
                return noData
            }
        },
    )

export const selectCityTypesByEditingCampaignCountry = (state: State): CityType[] => {
    const country = selectEditingCampaignCountry(state)
    if (!country) return []

    const countrySetting = selectCountrySetting(country)(state)

    if (!countrySetting) return []
    return countrySetting.city_types
}

export const selectBankDetailsByEditingCampaignCountry = (state: State): BankDetail | undefined => {
    const country = selectEditingCampaignCountry(state)
    if (!country) return undefined

    const countrySetting = selectCountrySetting(country)(state)

    if (!countrySetting) return undefined
    return countrySetting.partipost_bank_details
}

export const selectStripeSupportedByEditingCampaignCountry = (state: State): boolean => {
    const country = selectEditingCampaignCountry(state)
    if (!country) return false

    const countrySetting = selectCountrySetting(country)(state)

    if (!countrySetting) return false
    return countrySetting.stripe_payment_supported
}

export const selectCityTypesByReviewingCampaignCountry = (state: State): CityType[] => {
    const campaignUuid = selectSelectedCampaignUuid(state)
    if (!campaignUuid) return []
    const campaign = selectCampaign(campaignUuid)(state)
    if (!campaign) return []
    const country = campaign.country
    if (!country) return []
    const countryType = selectCountrySetting(country)(state)
    if (!countryType) return []
    return countryType.city_types
}

// todo: refactor this if youtube support search in the backend
export const unSearchablePlatforms = [PLATFORM_ENUM.OTHER, PLATFORM_ENUM.YOUTUBE]

export const selectPlatformOptions = createSelector(selectConstants, constants =>
    constants.platforms.map(platform => ({
        id: platform.id,
        name: platform.name,
        // TODO: consider using "exclude_from_profile" value from the backend, but the Common CM API doesnt have this value yet
        exclude_from_profile: unSearchablePlatforms.includes(platform.id),
    })),
)

export const selectOccupationOptions = (state: State): OccupationOption[] =>
    selectConstants(state).profileOptions.occupations

export const selectProfileTypeOptions = (state: State): ProfileTypeOption[] =>
    selectConstants(state).profileOptions.profile_types

export const selectFollowerTierOptions = (state: State): FollowerTierOption[] =>
    selectConstants(state).profileOptions.social_media_follower_number_tiers

export const makeFollowerTierOptionsByPlatformSelector = (platform: PLATFORM_ENUM) =>
    createSelector(
        selectEditingCampaignCountry,
        selectFollowerTierOptions,
        (country, followerTierOptions): FollowerTierOption[] => {
            if (!country) return []

            const result = followerTierOptions.filter(option => option.platform === platform)
            return result.length > 0
                ? result
                : followerTierOptions.filter(option => option.platform === PLATFORM_ENUM.IG)
        },
    )
export const selectFollowerTierFilterOptions = (platform: PLATFORM_ENUM, campaign?: CampaignState) =>
    createSelector(selectFollowerTierOptions, (followerTierOptions): FollowerTierFilterOption[] => {
        const options = followerTierOptions
            .filter(option => option.platform === platform)
            .map(option => ({
                label: parseFollowerOption(option, campaign?.country),
                value: option.id,
            }))
        return options
    })
export const selectIsModalOpen =
    (type: GLOBAL_MODAL_TYPE) =>
    (state: State): boolean =>
        Boolean(selectConstants(state).openModal && selectConstants(state).openModal.type === type)

export const selectBriefModalProps = (state: State): Pick<OpenModalState, 'stepId' | 'task'> | undefined => {
    const modalState = selectConstants(state).openModal
    if (!modalState) return

    return {
        task: modalState.task,
        stepId: modalState.stepId,
    }
}

export const selectModalCount = (state: State): number | undefined =>
    selectConstants(state).openModal && selectConstants(state).openModal.count

export const selectModalActionProps = (state: State): ActionModalProps | undefined =>
    selectConstants(state).openModal && selectConstants(state).openModal.actionProps

export const selectRegistrationLimits = (state: State): RegistrationConfigState => selectConstants(state).registration

export const selectRegistrationLengthLimits = (state: State): LengthLimitState => {
    const registration = selectConstants(state).registration
    if (registration.length_limits) return registration.length_limits
    else
        return {
            blocks: {
                description: 500,
                option: 100,
                title: 100,
            },
            registration_form: {
                title: 200,
                description: 1000,
            },
        }
}
/*
    t('banner.selectBannerRedirectLinkCampaignBriefMenuOption', 'Campaign brief screen')
    t('banner.selectBannerRedirectLinkPartiposterProfileMenuOption', 'Partiposter profile screen')
    t('banner.selectBannerRedirectLinkPartiposterSubmissionMenuOption', 'Partiposter submissions screen')
    t('banner.selectBannerRedirectLinkPollsFeedMenuOption', 'Polls feed screen')
    t('banner.selectBannerRedirectLinkCampaignTasksMenuOption', 'Campaign tasks screen')
    t('banner.selectBannerRedirectLinkUrlOption', 'URL')
*/
export const selectDeepLinkTypeOptions = (state: State): BannerOptionState[] =>
    selectConstants(state).banner.deeplink_types

export const selectBusinessTypesOptions = (state: State): { value: string; label: string }[] => {
    const options = selectConstants(state).profiles?.business_types
    const returnOptions: { value: string; label: string }[] = []
    if (options.length > 0) {
        options.map((option: { id: string; label: string }) => {
            let businessTypeKey = option.label
            switch (option.id) {
                case BUSINESS_TYPE.BRAND:
                    businessTypeKey = 'radioButton.manageTeams.companyType.brand'
                    break
                case BUSINESS_TYPE.AGENCY:
                    businessTypeKey = 'radioButton.manageTeams.companyType.agency'
                    break
            }
            returnOptions.push({ value: option.id, label: i18n.t(businessTypeKey) })
        })
    }
    return returnOptions
}
export const selectIndustryList = (state: State) => {
    const industrySettings = state.constants.brands?.industry
    return industrySettings.map((industry: { id: string; label: string }) => {
        let transferCode = ''
        switch (industry?.id) {
            case 'f&b_casual_dining':
                transferCode = 'FbCasualDining'
                break
            case 'fmcg_medicine_medical_devices':
                transferCode = 'FmcgMedical'
                break
            case 'fmcg_personal_hygiene_grooming':
                transferCode = 'FmcgPersonalHygiene'
                break
            case 'supermarket_grocery':
                transferCode = 'SupermarketShoppingMall'
                break
            case 'travel_hospitality_tours':
                transferCode = 'Travel'
                break
            default:
                const code = industry?.id.replace('&', '').replace('and', '').split('_')
                transferCode = code
                    .map((subCode: string) => subCode.charAt(0).toUpperCase() + subCode.slice(1))
                    .join('')
        }
        return {
            value: industry.id,
            label: i18n.t(`modalWindow.selectIndustry${transferCode}Option`),
        }
    })
}
export const selectMemberRange = (state: State) => {
    const memberRangeSettings = state.constants.brands?.member_range
    return memberRangeSettings.map((range: { id: string; label: string }) => {
        let label = ''
        switch (range?.id) {
            case '1 - 5':
                label = 'SmallOption'
                break
            case '6 - 10':
                label = 'MediumOption'
                break
            case '11 - 20':
                label = 'LargeOption'
                break
            case '> 20':
                label = 'VeryLargeOption'
                break
        }
        return {
            value: range.id,
            label: i18n.t(`modalWindow.selectMarketingTeamSize${label}`),
        }
    })
}
